import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, DatePicker, Form, InputNumber, Modal, Skeleton } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'
import { RENT_UNITS } from './const/rental-list-search.ts'

type RenewalValues = { endAt: Dayjs; agreedPrice: string; depositAmount?: string | null }

export function RenewRental({ order, onClose }: { order: RentalOrder; onClose: () => void }) {
  const [open, setOpen] = useState(true)
  const [form] = Form.useForm<RenewalValues>()
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const preview = useQuery(trpc.order.rental.renewalPreview.queryOptions({ rentalOrderId: order.id }))
  const save = useMutation(trpc.order.rental.renew.mutationOptions())
  const startAt = dayjs(preview.data?.startAt)
  return (
    <Modal
      open={open}
      title="续租为新订单"
      width="40rem"
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      destroyOnHidden
      mask={{ closable: false }}
      closable={!save.isPending}
      keyboard={!save.isPending}
      confirmLoading={save.isPending}
      cancelButtonProps={{ disabled: save.isPending }}
      okText="创建续租订单"
      okButtonProps={{ disabled: !preview.data || preview.isError }}
      onOk={() => form.submit()}
    >
      {preview.isError && (
        <Alert
          showIcon
          type="error"
          title={preview.error.message}
          action={<Button onClick={() => void preview.refetch()}>重试</Button>}
        />
      )}
      {preview.isPending && <Skeleton active paragraph={{ rows: 4 }} />}
      {preview.data && (
        <Form<RenewalValues>
          form={form}
          layout="vertical"
          disabled={save.isPending}
          initialValues={{
            endAt: startAt.add(1, 'year'),
            agreedPrice: preview.data.agreedPrice,
            depositAmount: preview.data.depositAmount
          }}
          onFinish={async (values) => {
            try {
              const result = await save.mutateAsync({
                rentalOrderId: order.id,
                ...values,
                endAt: values.endAt.format('YYYY-MM-DD'),
                idempotencyKey
              })
              await cache.invalidateQueries(trpc.order.rental.list.queryFilter())
              void message.success(`续租订单 ${result.orderNo}（#${result.id}）已创建，请到租赁列表保存新合同`, 8)
              setOpen(false)
            } catch (error) {
              void message.error(error instanceof Error ? error.message : '续租失败')
            }
          }}
        >
          <p className="text-content-muted mb-4">
            原订单 {order.orderNo} · {order.customerName}
          </p>
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <Form.Item label="新租期起始日" extra="承接原订单截止日">
              <DatePicker className="w-full" value={startAt} disabled />
            </Form.Item>
            <Form.Item
              name="endAt"
              label="新租期终止日（不含）"
              rules={[
                { required: true, message: '请选择新租期终止日' },
                {
                  validator: async (_, value: Dayjs | undefined) => {
                    if (value && !value.isAfter(startAt, 'day')) throw new Error('终止日须晚于起始日')
                  }
                }
              ]}
            >
              <DatePicker className="w-full" disabledDate={(date) => !date.isAfter(startAt, 'day')} />
            </Form.Item>
            <Form.Item
              name="agreedPrice"
              label={`新租金单价（元 / ${RENT_UNITS[order.rentType]}）`}
              rules={[{ required: true, message: '请填写新租金单价' }]}
            >
              <InputNumber className="w-full!" stringMode precision={2} min="0" />
            </Form.Item>
            <Form.Item name="depositAmount" label="新押金（元）" extra="选填，留空不生成押金账单">
              <InputNumber className="w-full!" stringMode precision={2} min="0" placeholder="请输入新押金" />
            </Form.Item>
          </div>
          <Alert
            showIcon
            type="info"
            title="新订单独立签约、核费和收款"
            description="旧押金不会转入新订单。需退回时，请在原押金账单释放并登记实际退款。"
          />
        </Form>
      )}
    </Modal>
  )
}
